import React, { useState } from 'react';
import { T, TopNav, Footer, TierBadge, MonoLabel, Card, Btn, Field, InputField, TextareaField, Alert } from './ui';
import { ESSAY_TYPES } from '../data/prompts';
import { generateIdeaBankForPrompt, loadGeneratedIdeas, saveGeneratedIdea } from '../data/generatedIdeas';
import { loadIeltsSessions, saveIeltsSessions } from '../data/storage';
import LiveSession from './LiveSession';
import AcademyAdmin from './academy/AcademyAdmin';

export default function TeacherPanel({ branding, tiers, candidates, prompts, formulas, onSaveBranding, onSaveTiers, onSaveCandidates, onSavePrompts, onSaveFormulas, onBack, onLogout }) {
  const [tab, setTab] = useState('tiers');

  // Candidates
  const [cands, setCands] = useState(candidates);
  const [newC,  setNewC]  = useState({ email:'', name:'', tier:'trial' });
  const [bulkE, setBulkE] = useState('');
  const [editC, setEditC] = useState(null);

  // Branding
  const [brand, setBrand] = useState(branding);


  // Tiers
  const [tierData, setTierData] = useState(tiers);

  // Prompts
  const [activeType,      setActiveType]      = useState('discuss');
  const [editPId,         setEditPId]         = useState(null);
  const [newP,            setNewP]            = useState({ title:'', text:'' });
  const [addingP,         setAddingP]         = useState(false);
  const [generatingForId, setGeneratingForId] = useState(null);
  const [generatedIdeas,  setGeneratedIdeas]  = useState(() => loadGeneratedIdeas());
  const [syncMsg,         setSyncMsg]         = useState('');

  // Formulas
  const [fType,       setFType]      = useState('discuss');
  const [editingFKey, setEditingFKey]= useState(null);
  const [editingFVal, setEditingFVal]= useState('');

  // IELTS Sessions
  const [sessions, setSessions] = useState(() => loadIeltsSessions());
  const [editingSessId, setEditingSessId] = useState(null);

  // ── CANDIDATES ─────────────────────────────────────────────────
  function saveCands(list) { setCands(list); onSaveCandidates(list); }

  function addSingle() {
    if (!newC.email.trim() || !newC.email.includes('@')) return;
    const e = newC.email.trim().toLowerCase();
    if (cands.find(c=>c.email.toLowerCase()===e)) return;
    saveCands([...cands, { email:e, name:newC.name.trim(), tier:newC.tier, used:0, active:true }]);
    setNewC({ email:'', name:'', tier:'trial' });
  }

  function addBulk() {
    const lines = bulkE.split(/[\n,;]+/).map(e=>e.trim().toLowerCase()).filter(e=>e.includes('@'));
    const existing = cands.map(c=>c.email.toLowerCase());
    const newOnes = lines.filter(e=>!existing.includes(e)).map(e=>({ email:e, name:'', tier:'trial', used:0, active:true }));
    if (newOnes.length) saveCands([...cands, ...newOnes]);
    setBulkE('');
  }

  // ── IDEA BANK GENERATION ────────────────────────────────────────
  async function handleSaveNewPrompt() {
    if (!newP.title.trim() || !newP.text.trim()) return;
    const newId     = Date.now();
    const newPrompt = { id: newId, ...newP };
    const et        = ESSAY_TYPES.find(e => e.id === activeType);
    const upd       = { ...prompts, [activeType]: [...(prompts[activeType] || []), newPrompt] };
    onSavePrompts(upd);
    setNewP({ title: '', text: '' });
    setAddingP(false);
    setGeneratingForId(newId);
    try {
      const idea = await generateIdeaBankForPrompt(newPrompt, et?.label || activeType);
      saveGeneratedIdea(newId, idea);
      setGeneratedIdeas(prev => ({ ...prev, [String(newId)]: idea }));
    } catch (e) { console.error('Idea bank generation failed:', e); }
    setGeneratingForId(null);
  }

  async function handleRegenerateIdeas(p) {
    const et = ESSAY_TYPES.find(e => e.id === activeType);
    setGeneratingForId(p.id);
    try {
      const idea = await generateIdeaBankForPrompt(p, et?.label || activeType);
      saveGeneratedIdea(p.id, idea);
      setGeneratedIdeas(prev => ({ ...prev, [String(p.id)]: idea }));
    } catch (e) { console.error('Idea bank regeneration failed:', e); }
    setGeneratingForId(null);
  }

  // ── TIERS ───────────────────────────────────────────────────────
  function saveTierData(key, val) {
    const n = parseInt(val);
    if (isNaN(n) || n < 1) return;
    const updated = { ...tierData, [key]:{ ...tierData[key], limit:n } };
    setTierData(updated); onSaveTiers(updated);
  }

  // ── BRANDING ────────────────────────────────────────────────────
  function saveBrand() { setBrand(b=>b); onSaveBranding(brand); }

  // ── FORMULAS ────────────────────────────────────────────────────
  const [fData, setFData] = useState(formulas);

  function updateFOpt(typeId, pi, si, oi, val) {
    const updated = { ...fData, [typeId]: fData[typeId].map((p,pid)=>
      pid!==pi?p:{ ...p, sentences: p.sentences.map((s,sid)=>
        sid!==si?s:{ ...s, opts: s.opts.map((o,oid)=>oid!==oi?o:val) }
      )}
    )};
    setFData(updated); onSaveFormulas(updated); setEditingFKey(null);
  }

  function updateFPurpose(typeId, pi, si, val) {
    const updated = { ...fData, [typeId]: fData[typeId].map((p,pid)=>
      pid!==pi?p:{ ...p, sentences: p.sentences.map((s,sid)=>
        sid!==si?s:{ ...s, purpose:val }
      )}
    )};
    setFData(updated); onSaveFormulas(updated);
  }

  // ── IELTS SESSIONS ─────────────────────────────────────────────
  const ALL_DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const TIMEZONES = [
    'UAE (Gulf Standard Time GMT+4)',
    'Egypt (Eastern European Time GMT+2)',
    'Saudi Arabia (AST GMT+3)',
    'UK (GMT/BST)',
    'USA Eastern (EST/EDT)',
  ];

  function saveSessions(list) { setSessions(list); saveIeltsSessions(list); }

  function addSession() {
    const newId = Date.now();
    const newSess = { id:newId, days:['Mon','Tue','Wed','Thu'], startTime:'20:00', endTime:'22:00', timezone:TIMEZONES[0], label:'New Group', status:'active' };
    saveSessions([...sessions, newSess]);
    setEditingSessId(newId);
  }

  function deleteSession(id) { saveSessions(sessions.filter(s => s.id !== id)); }

  function updateSession(id, field, value) {
    saveSessions(sessions.map(s => s.id === id ? { ...s, [field]: value } : s));
  }

  function toggleDay(id, day) {
    const sess = sessions.find(s => s.id === id);
    if (!sess) return;
    const days = sess.days.includes(day) ? sess.days.filter(d => d !== day) : [...sess.days, day];
    updateSession(id, 'days', days);
  }

  const TABS = [
    // Legacy IELTS Students tab hidden — students are managed in Academy → Students. Candidate data layer kept for trial/login flows.
    ['tiers','⚙ Essay & Test Limits'],
    ['branding','🎨 Branding'],
    ['prompts','📝 Prompts'],
    ['formulas','✏️ Edit Formulas'],
    ['schedule','📅 Class Schedule'],
    ['academy','🏫 Academy'],
    ['live','📡 Live Sessions'],
  ];

  return (
    <div style={{ minHeight:'100vh', background:'#f0f4f8', display:'flex', flexDirection:'column' }}>
      <TopNav branding={branding} onBack={onBack} backLabel="Home" onLogoClick={onBack}
        right={<button onClick={onLogout} style={{ background:'rgba(220,50,50,0.12)', border:'1px solid rgba(220,50,50,0.25)', color:'#c0392b', padding:'5px 12px', borderRadius:7, fontSize:12, cursor:'pointer' }}>Sign Out</button>}
      />
      <div style={{ maxWidth:920, margin:'0 auto', padding:'22px 18px', flex:1, width:'100%' }}>
        <MonoLabel color={T.gold} style={{ marginBottom:4 }}>Admin Panel</MonoLabel>
        <h1 style={{ fontSize:24, marginBottom:18 }}>Teacher Dashboard</h1>

        {/* Tab bar */}
        <div style={{ display:'flex', borderBottom:`1px solid ${T.border}`, marginBottom:20, overflowX:'auto', flexWrap:'nowrap' }}>
          {TABS.map(([k,l])=>(
            <button key={k} onClick={()=>setTab(k)} style={{ border:'none', borderBottom:`2px solid ${tab===k?T.gold:'transparent'}`, background:'none', color:tab===k?T.gold:T.muted, padding:'9px 14px', fontSize:13, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", whiteSpace:'nowrap' }}>{l}</button>
          ))}
        </div>

        {/* ── CANDIDATES ── */}
        {tab==='candidates' && (
          <div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:16 }}>
              {[['Total',cands.length],['Trial',cands.filter(c=>c.tier==='trial').length],['Standard',cands.filter(c=>c.tier==='standard').length],['Premium',cands.filter(c=>c.tier==='premium').length]].map(([l,v])=>(
                <div key={l} style={{ background:'#f7f9fc', borderRadius:8, padding:12, textAlign:'center' }}>
                  <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:26, fontWeight:700, color:T.gold, lineHeight:1 }}>{v}</div>
                  <div style={{ fontSize:11, color:'#5a6a88', marginTop:3 }}>{l}</div>
                </div>
              ))}
            </div>

            {cands.map((c,i)=>(
              <Card key={c.email} style={{ marginBottom:8, padding:'12px 15px' }}>
                {editC===i ? (
                  <div>
                    <div style={{ fontSize:12, color:'#5a6a88', marginBottom:8, fontFamily:"'JetBrains Mono',monospace" }}>{c.email}</div>
                    <div style={{ marginBottom:10 }}>
                      <MonoLabel style={{ marginBottom:5 }}>Subscription Tier</MonoLabel>
                      <div style={{ display:'flex', gap:6 }}>
                        {['trial','standard','premium'].map(t=>(
                          <button key={t} onClick={()=>saveCands(cands.map((x,j)=>j===i?{...x,tier:t}:x))}
                            style={{ flex:1, padding:'7px', borderRadius:6, border:`1px solid ${c.tier===t?T.gold:T.border}`, background:c.tier===t?'rgba(200,149,10,.15)':'transparent', color:c.tier===t?T.gold:T.muted, cursor:'pointer', fontSize:12, fontWeight:500 }}>
                            {t.charAt(0).toUpperCase()+t.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                      <button onClick={()=>saveCands(cands.map((x,j)=>j===i?{...x,usedSmart:0,usedTest:0}:x))} style={{ background:'#f7f9fc', border:'1px solid #dde3ed', color:'#5a6a88', padding:'5px 11px', borderRadius:6, cursor:'pointer', fontSize:11.5 }}>Reset Counts</button>
                      <button onClick={()=>saveCands(cands.map((x,j)=>j===i?{...x,active:!x.active}:x))} style={{ background:'transparent', border:`1px solid ${c.active?T.red:T.green}44`, color:c.active?T.red:T.green, padding:'5px 11px', borderRadius:6, cursor:'pointer', fontSize:11.5 }}>{c.active?'Deactivate':'Activate'}</button>
                      <button onClick={()=>saveCands(cands.filter((_,j)=>j!==i))} style={{ background:'transparent', border:`1px solid ${T.red}44`, color:T.red, padding:'5px 11px', borderRadius:6, cursor:'pointer', fontSize:11.5 }}>Remove</button>
                      <button onClick={()=>setEditC(null)} style={{ background:T.gold, color:'#ffffff', border:'none', padding:'5px 14px', borderRadius:6, cursor:'pointer', fontSize:11.5, fontWeight:600 }}>Done</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:500, marginBottom:2 }}>{c.name||c.email}</div>
                      <div style={{ fontSize:11, color:'#5a6a88', fontFamily:"'JetBrains Mono',monospace" }}>
                        {c.name?c.email:''}{c.name?' · ':''}
                        ✍️ {c.usedSmart||0} smart
                        {' · '}
                        ⏱ {c.usedTest||0} tests
                        {!c.active&&<span style={{ color:'#c0392b', marginLeft:8 }}>Inactive</span>}
                      </div>
                    </div>
                    <TierBadge tier={c.tier}/>
                    <button onClick={()=>setEditC(i)} style={{ background:'#f7f9fc', border:'1px solid #dde3ed', color:'#5a6a88', padding:'5px 11px', borderRadius:6, cursor:'pointer', fontSize:12 }}>Manage</button>
                  </div>
                )}
              </Card>
            ))}

            <div style={{ marginTop:14 }}>
              <div style={{ fontSize:13.5, fontWeight:500, marginBottom:10 }}>Add New Candidate</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto auto', gap:7, marginBottom:10, flexWrap:'wrap' }}>
                <input value={newC.email} onChange={e=>setNewC(c=>({...c,email:e.target.value}))} placeholder="Email *"
                  style={{ padding:'8px 11px', border:'1px solid #dde3ed', borderRadius:7, background:'#f7f9fc', color:'#1a2233', fontSize:13, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:'none' }}/>
                <input value={newC.name} onChange={e=>setNewC(c=>({...c,name:e.target.value}))} placeholder="Name (optional)"
                  style={{ padding:'8px 11px', border:'1px solid #dde3ed', borderRadius:7, background:'#f7f9fc', color:'#1a2233', fontSize:13, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:'none' }}/>
                <select value={newC.tier} onChange={e=>setNewC(c=>({...c,tier:e.target.value}))}
                  style={{ padding:'8px 9px', border:'1px solid #dde3ed', borderRadius:7, background:'#f7f9fc', color:'#1a2233', fontSize:13, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:'none' }}>
                  <option value="trial">Trial</option><option value="standard">Standard</option><option value="premium">Premium</option>
                </select>
                <button onClick={addSingle} style={{ background:T.gold, color:'#ffffff', border:'none', padding:'8px 16px', borderRadius:7, fontWeight:600, fontSize:13, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Add</button>
              </div>
              <div style={{ fontSize:13.5, fontWeight:500, marginBottom:6 }}>Paste Multiple Emails (one per line)</div>
              <textarea value={bulkE} onChange={e=>setBulkE(e.target.value)} rows={3} placeholder="student1@email.com&#10;student2@email.com&#10;(All added as Trial by default)"
                style={{ width:'100%', padding:'9px 11px', border:'1px solid #dde3ed', borderRadius:7, background:'#f7f9fc', color:'#1a2233', fontSize:13, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:'none', resize:'vertical', marginBottom:8 }}/>
              <button onClick={addBulk} style={{ background:'#f7f9fc', border:'1px solid #dde3ed', color:'#5a6a88', padding:'7px 16px', borderRadius:6, fontSize:12, cursor:'pointer' }}>Add All</button>
            </div>
          </div>
        )}

        {/* ── TIERS ── */}
        {tab==='tiers' && (
          <div>
            <Alert type="info">Set essay limits separately for Smart Writing Mode and Test Mode. Enter 9999 for unlimited. Changes apply immediately.</Alert>

            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:0, marginBottom:8 }}>
              <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, letterSpacing:'.12em', textTransform:'uppercase', color:'#9aa3b8', padding:'8px 12px' }}>Tier</div>
              <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, letterSpacing:'.12em', textTransform:'uppercase', color:'#1a5fa8', padding:'8px 12px', textAlign:'center' }}>✍️ Smart Writing</div>
              <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, letterSpacing:'.12em', textTransform:'uppercase', color:'#b8620a', padding:'8px 12px', textAlign:'center' }}>⏱ Test Mode</div>
            </div>

            {Object.entries(tierData).map(([key,t])=>(
              <Card key={key} style={{ marginBottom:10, padding:'14px 16px' }}>
                <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:12, alignItems:'center' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <TierBadge tier={key}/>
                    <div style={{ fontSize:13.5, fontWeight:600, color:'#1a2233' }}>{t.label} Plan</div>
                  </div>
                  <div style={{ textAlign:'center' }}>
                    <input type="number" defaultValue={t.limitSmart ?? t.limit ?? 1} min="1"
                      onBlur={e => {
                        const n = parseInt(e.target.value)||1;
                        const updated = { ...tierData, [key]:{ ...tierData[key], limitSmart:n } };
                        setTierData(updated); onSaveTiers(updated);
                      }}
                      style={{ width:'90px', padding:'7px 10px', border:'1.5px solid #c0d4f0', borderRadius:7, background:'#f0f6ff', color:'#1a5fa8', fontSize:14, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:'none', textAlign:'center', fontWeight:600 }}/>
                    <div style={{ fontSize:10, color:'#9aa3b8', marginTop:3 }}>
                      {(t.limitSmart??t.limit??1) >= 9999 ? 'Unlimited' : `${t.limitSmart??t.limit??1} sessions`}
                    </div>
                  </div>
                  <div style={{ textAlign:'center' }}>
                    <input type="number" defaultValue={t.limitTest ?? 1} min="1"
                      onBlur={e => {
                        const n = parseInt(e.target.value)||1;
                        const updated = { ...tierData, [key]:{ ...tierData[key], limitTest:n } };
                        setTierData(updated); onSaveTiers(updated);
                      }}
                      style={{ width:'90px', padding:'7px 10px', border:'1.5px solid #fde0b0', borderRadius:7, background:'#fff8f0', color:'#b8620a', fontSize:14, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:'none', textAlign:'center', fontWeight:600 }}/>
                    <div style={{ fontSize:10, color:'#9aa3b8', marginTop:3 }}>
                      {(t.limitTest??1) >= 9999 ? 'Unlimited' : `${t.limitTest??1} tests`}
                    </div>
                  </div>
                </div>
              </Card>
            ))}

            <div style={{ background:'#f7f9fc', border:'1px solid #dde3ed', borderRadius:8, padding:'12px 16px', marginTop:8 }}>
              <div style={{ fontSize:12.5, color:'#5a6a88', lineHeight:1.8 }}>
                <strong style={{color:'#1a2233'}}>Current setup:</strong><br/>
                🟢 <strong>Trial</strong> — 1 Smart Writing (Discuss Both Views only) · 1 Test Mode · WhatsApp popup after each<br/>
                🔵 <strong>Standard</strong> — Unlimited Smart Writing · 12 Test Mode sessions<br/>
                🟡 <strong>Premium</strong> — Unlimited Smart Writing · 25 Test Mode sessions<br/>
                Enter <strong>9999</strong> for unlimited in any field.
              </div>
            </div>
          </div>
        )}

        {/* ── BRANDING ── */}
        {tab==='branding' && (() => {
          const ann = brand.announcement || {};
          const ANN_COLORS = [
            { id:'gold', label:'Gold', swatch:'#b8860b' },
            { id:'navy', label:'Navy', swatch:'#1a2a4a' },
            { id:'green',label:'Green',swatch:'#1a7a4a' },
            { id:'red',  label:'Red',  swatch:'#c0392b' },
          ];
          const COLOR_PREVIEW = { gold:'#b8860b', navy:'#1a2a4a', green:'#1a7a4a', red:'#c0392b' };
          function setAnn(fn) { setBrand(b => ({ ...b, announcement: fn(b.announcement || {}) })); }
          const dateLabel = ann.groupDate ? new Date(ann.groupDate).toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long'}) : '';
          const previewMsg = ann.groupDate ? `New IELTS group starting ${dateLabel} — limited places available!` : (ann.message || '');

          return (
          <div>
            <Alert type="info">Changes appear across the entire platform immediately after saving.</Alert>
            <Card>
              <Field label="Platform Logo Text">
                <InputField value={brand.logo||''} onChange={v=>setBrand(b=>({...b,logo:v}))} placeholder="IELTSCOMPASS"/>
              </Field>
              <Field label="Tagline (e.g. with Mr. Ahmed Salem)">
                <InputField value={brand.tagline||''} onChange={v=>setBrand(b=>({...b,tagline:v}))} placeholder="with Mr. Ahmed Salem"/>
              </Field>
              <Field label="Motto (shown below logo in italic gold)">
                <InputField value={brand.motto||''} onChange={v=>setBrand(b=>({...b,motto:v}))} placeholder="Navigate your path to success."/>
              </Field>
              <Field label="Phone Number 1">
                <InputField value={brand.phone1||''} onChange={v=>setBrand(b=>({...b,phone1:v}))} placeholder="00201008182425"/>
              </Field>
              <Field label="Phone Number 2">
                <InputField value={brand.phone2||''} onChange={v=>setBrand(b=>({...b,phone2:v}))} style={{ marginBottom:14 }}/>
              </Field>
              <Btn onClick={saveBrand} variant="primary" style={{ width:'100%', padding:11 }}>Save Branding</Btn>
            </Card>

            {/* ── ANNOUNCEMENT BANNER ── */}
            <Card style={{ marginTop:16 }}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:17, fontWeight:700, color:'#1a2a4a', marginBottom:14 }}>🔔 Announcement Banner</div>
              <Alert type="info">
                Banner shows automatically 7 days before the group start date and hides on the start date. Leave date empty to show the banner permanently until you turn it off. Only shown to visitors and trial users — never to paid students or teachers.
              </Alert>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div style={{ gridColumn:'1/-1' }}>
                  <Field label="Show announcement banner">
                    <div style={{ display:'flex', gap:8 }}>
                      {[['On — banner shows',true],['Off — hidden',false]].map(([lbl,val])=>(
                        <button key={String(val)} onClick={()=>setAnn(a=>({...a,active:val}))}
                          style={{ flex:1, padding:'9px', borderRadius:8, cursor:'pointer', border:`1.5px solid ${ann.active===val?T.gold:T.border}`, background:ann.active===val?T.gold+'22':'transparent', color:ann.active===val?T.gold:T.muted, fontSize:12.5, fontWeight:ann.active===val?600:400 }}>
                          {lbl}
                        </button>
                      ))}
                    </div>
                  </Field>
                </div>

                <div style={{ gridColumn:'1/-1' }}>
                  <Field label="Message text (shown when no group date set)">
                    <TextareaField value={ann.message||''} onChange={v=>setAnn(a=>({...a,message:v}))} rows={2} placeholder="New IELTS group starting Monday — limited places available!"/>
                  </Field>
                </div>

                <Field label="Next group start date">
                  <input type="date" value={ann.groupDate||''} onChange={e=>setAnn(a=>({...a,groupDate:e.target.value}))}
                    style={{ width:'100%', padding:'9px 12px', border:`1px solid ${T.border}`, borderRadius:9, background:'#f8f9fb', color:'#1a2233', fontSize:13 }}/>
                  {ann.groupDate && <div style={{ fontSize:11, color:'#9aa3b8', marginTop:4 }}>Shows from {new Date(new Date(ann.groupDate).setDate(new Date(ann.groupDate).getDate()-7)).toLocaleDateString()} · Hides on {dateLabel}</div>}
                </Field>

                <Field label="Banner colour">
                  <div style={{ display:'flex', gap:8 }}>
                    {ANN_COLORS.map(c=>(
                      <button key={c.id} onClick={()=>setAnn(a=>({...a,color:c.id}))}
                        style={{ flex:1, padding:'7px 4px', borderRadius:8, cursor:'pointer', border:`2px solid ${ann.color===c.id?c.swatch:T.border}`, background:ann.color===c.id?c.swatch+'22':'transparent', fontSize:11.5, color:c.swatch, fontWeight:ann.color===c.id?700:400, display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                        <div style={{ width:14,height:14,borderRadius:'50%',background:c.swatch }}/>
                        {c.label}
                      </button>
                    ))}
                  </div>
                </Field>
              </div>

              {/* Preview */}
              <div style={{ marginTop:12 }}>
                <div style={{ fontSize:12, fontWeight:600, color:'#1a2233', marginBottom:6 }}>Preview:</div>
                <div style={{ borderRadius:9, overflow:'hidden', border:`1px solid ${T.border}` }}>
                  {ann.active ? (
                    <div style={{ background:COLOR_PREVIEW[ann.color]||'#b8860b', color:'#fff', padding:'10px 14px', display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                      <span style={{ fontSize:18 }}>🔔</span>
                      <div style={{ flex:1, fontSize:13, fontWeight:600 }}>{previewMsg || '(no message yet)'}</div>
                      <div style={{ background:'#25d366', color:'#fff', padding:'6px 12px', borderRadius:7, fontSize:12, fontWeight:700 }}>Reserve My Place</div>
                      <div style={{ width:24,height:24,borderRadius:'50%',background:'rgba(0,0,0,0.15)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13 }}>×</div>
                    </div>
                  ) : (
                    <div style={{ padding:14, textAlign:'center', color:'#9aa3b8', fontSize:13 }}>Banner is currently hidden</div>
                  )}
                </div>
              </div>

              <div style={{ marginTop:14 }}>
                <Btn variant="primary" onClick={saveBrand} style={{ width:'100%', padding:11 }}>Save Branding &amp; Announcement</Btn>
              </div>
            </Card>
          </div>
          );
        })()}


        {/* ── PROMPTS ── */}
        {tab==='prompts' && (
          <div>
            <div style={{ display:'flex', gap:7, flexWrap:'wrap', marginBottom:16 }}>
              {ESSAY_TYPES.map(et=>(
                <button key={et.id} onClick={()=>{setActiveType(et.id);setEditPId(null);setAddingP(false);}}
                  style={{ padding:'6px 12px', borderRadius:7, border:`1px solid ${activeType===et.id?et.accent:T.border}`, background:activeType===et.id?et.color+'22':'transparent', color:activeType===et.id?et.accent:T.muted, cursor:'pointer', fontSize:12.5 }}>
                  {et.icon} {et.id}
                </button>
              ))}
            </div>

            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
              <div style={{ fontSize:12, color:'#5a6a88' }}>{(prompts[activeType]||[]).length} prompts for this type</div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                {syncMsg && <span style={{ fontSize:11.5, color: syncMsg.startsWith('✓') ? '#1a7a4a' : '#b8860b' }}>{syncMsg}</span>}
                <Btn onClick={() => {
                  setSyncMsg('Syncing…');
                  onSavePrompts(prompts);
                  setTimeout(() => { setSyncMsg('✓ Synced to cloud'); setTimeout(() => setSyncMsg(''), 3000); }, 1500);
                }} small>☁ Sync to Cloud</Btn>
                <Btn onClick={()=>setAddingP(true)} variant="primary" small>+ Add Prompt</Btn>
              </div>
            </div>

            {addingP && (
              <Card style={{ marginBottom:12, border:`1px solid ${T.gold}44` }}>
                <div style={{ fontSize:13.5, fontWeight:600, color:T.gold, marginBottom:10 }}>New Prompt</div>
                <Field label="Title"><InputField value={newP.title} onChange={v=>setNewP(p=>({...p,title:v}))} placeholder="e.g. Social Media and Youth"/></Field>
                <Field label="Full Question Text"><TextareaField value={newP.text} onChange={v=>setNewP(p=>({...p,text:v}))} placeholder="Some people believe… Others think… Discuss both views and give your own opinion." style={{ marginBottom:10 }}/></Field>
                <div style={{ display:'flex', gap:8 }}>
                  <Btn onClick={handleSaveNewPrompt} variant="primary" style={{ flex:1 }}>Save &amp; Generate Ideas</Btn>
                  <Btn onClick={()=>setAddingP(false)}>Cancel</Btn>
                </div>
              </Card>
            )}

            {(prompts[activeType]||[]).map((p,i)=>(
              <Card key={p.id} style={{ marginBottom:8, padding:'12px 14px' }}>
                {editPId===p.id ? (
                  <div>
                    <input value={p.title} onChange={e=>onSavePrompts({...prompts,[activeType]:prompts[activeType].map((x,j)=>j===i?{...x,title:e.target.value}:x)})}
                      style={{ width:'100%', padding:'7px 10px', border:'1px solid #dde3ed', borderRadius:6, background:'#f7f9fc', color:'#1a2233', fontSize:13, fontWeight:600, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:'none', marginBottom:7 }}/>
                    <textarea value={p.text} rows={3} onChange={e=>onSavePrompts({...prompts,[activeType]:prompts[activeType].map((x,j)=>j===i?{...x,text:e.target.value}:x)})}
                      style={{ width:'100%', padding:'7px 10px', border:'1px solid #dde3ed', borderRadius:6, background:'#f7f9fc', color:'#1a2233', fontSize:13, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:'none', resize:'vertical', marginBottom:8 }}/>
                    <Btn onClick={()=>setEditPId(null)} variant="primary" small>Done</Btn>
                  </div>
                ) : (
                  <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:600, marginBottom:3 }}>{p.title}</div>
                      <div style={{ fontSize:11.5, color:'#5a6a88', lineHeight:1.55 }}>{p.text.length>90?p.text.slice(0,90)+'…':p.text}</div>
                      {/* Idea bank status */}
                      {generatingForId===p.id ? (
                        <div style={{ marginTop:5, display:'flex', alignItems:'center', gap:5 }}>
                          <div style={{ width:10, height:10, border:'2px solid #b8860b', borderTopColor:'transparent', borderRadius:'50%', animation:'spin 1s linear infinite' }}/>
                          <span style={{ fontSize:10.5, color:'#b8860b', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Generating idea bank…</span>
                          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
                        </div>
                      ) : generatedIdeas[String(p.id)] ? (
                        <div style={{ marginTop:5, fontSize:10.5, color:'#1a7a4a', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                          💡 Idea bank ready · Generated {generatedIdeas[String(p.id)].generatedAt}
                        </div>
                      ) : !p.ideaKey ? (
                        <div style={{ marginTop:5, fontSize:10.5, color:'#9aa3b8', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                          💡 No idea bank yet
                        </div>
                      ) : null}
                    </div>
                    <div style={{ display:'flex', gap:5, flexShrink:0, flexWrap:'wrap', justifyContent:'flex-end' }}>
                      <Btn onClick={()=>setEditPId(p.id)} small>Edit</Btn>
                      {i>=2&&<Btn onClick={()=>onSavePrompts({...prompts,[activeType]:prompts[activeType].filter((_,j)=>j!==i)})} variant="danger" small>Del</Btn>}
                      <Btn onClick={()=>handleRegenerateIdeas(p)} small disabled={generatingForId===p.id}>
                        {generatingForId===p.id ? '…' : generatedIdeas[String(p.id)] ? '🔄 Regen Ideas' : '💡 Gen Ideas'}
                      </Btn>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* ── FORMULAS ── */}
        {tab==='formulas' && (
          <div>
            <div style={{ display:'flex', gap:7, flexWrap:'wrap', marginBottom:16 }}>
              {ESSAY_TYPES.map(et=>(
                <button key={et.id} onClick={()=>setFType(et.id)}
                  style={{ padding:'6px 12px', borderRadius:7, border:`1px solid ${fType===et.id?et.accent:T.border}`, background:fType===et.id?et.color+'22':'transparent', color:fType===et.id?et.accent:T.muted, cursor:'pointer', fontSize:12.5 }}>
                  {et.icon} {et.id}
                </button>
              ))}
            </div>
            <Alert type="info">Click any formula option to edit it. Changes save automatically and appear immediately for candidates.</Alert>

            {(fData[fType]||[]).map((para,pi)=>{
              const et = ESSAY_TYPES.find(t=>t.id===fType)||ESSAY_TYPES[0];
              return (
                <div key={para.id} style={{ marginBottom:20 }}>
                  <div style={{ borderLeft:`3px solid ${et.accent}`, padding:'8px 13px', background:'#f7f9fc', marginBottom:10, borderRadius:'0 7px 7px 0' }}>
                    <div style={{ fontSize:14, fontWeight:600 }}>{para.label}</div>
                  </div>
                  {para.sentences.map((s,si)=>(
                    <Card key={s.id} style={{ marginBottom:8 }}>
                      <div style={{ display:'flex', gap:8, marginBottom:7, alignItems:'center' }}>
                        <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:9, color:et.accent, background:'#f7f9fc', padding:'2px 7px', borderRadius:4 }}>{s.num}</div>
                        <div style={{ fontSize:13, fontWeight:600, color:s.role.startsWith('★')?et.accent:'#1a2233' }}>{s.role}</div>
                      </div>
                      <textarea value={s.purpose} rows={2} onChange={e=>updateFPurpose(fType,pi,si,e.target.value)}
                        style={{ width:'100%', padding:'7px 10px', border:'1px solid #dde3ed', borderRadius:6, background:'#f7f9fc', color:'#5a6a88', fontSize:11.5, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:'none', resize:'none', marginBottom:8 }}/>
                      <MonoLabel style={{ marginBottom:6 }}>Formula Options (click to edit):</MonoLabel>
                      {s.opts.map((opt,oi)=>{
                        const fKey = `${fType}-${pi}-${si}-${oi}`;
                        return editingFKey===fKey ? (
                          <div key={oi} style={{ display:'flex', gap:7, marginBottom:6 }}>
                            <textarea value={editingFVal} onChange={e=>setEditingFVal(e.target.value)} rows={2}
                              style={{ flex:1, padding:'7px 10px', border:`1px solid ${et.accent}`, borderRadius:6, background:'#f7f9fc', color:'#1a2233', fontSize:12.5, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:'none', resize:'vertical' }}/>
                            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                              <Btn onClick={()=>updateFOpt(fType,pi,si,oi,editingFVal)} variant="primary" small>Save</Btn>
                              <Btn onClick={()=>setEditingFKey(null)} small>Cancel</Btn>
                            </div>
                          </div>
                        ) : (
                          <div key={oi} onClick={()=>{ setEditingFKey(fKey); setEditingFVal(opt); }}
                            style={{ background:'#f7f9fc', border:'1px solid #e2e6ed', borderRadius:6, padding:'8px 11px', marginBottom:5, cursor:'pointer', display:'flex', gap:8, alignItems:'flex-start', transition:'border-color .15s' }}
                            onMouseEnter={e=>e.currentTarget.style.borderColor=et.accent}
                            onMouseLeave={e=>e.currentTarget.style.borderColor='#dde3ed'}>
                            <MonoLabel color={et.accent} style={{ flexShrink:0, marginTop:2 }}>OPT {oi+1}</MonoLabel>
                            <div style={{ fontSize:12.5, lineHeight:1.6, flex:1, color:'#1a2233' }}>{opt}</div>
                            <div style={{ color:'#9aa3b8', fontSize:12, flexShrink:0 }}>✎</div>
                          </div>
                        );
                      })}
                    </Card>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* ── CLASS SCHEDULE ── */}
        {tab === 'schedule' && (
          <div>
            <Alert type="info">
              Manage IELTS group class sessions. Active sessions appear on the public Courses page. Inactive sessions are hidden from students but saved for easy reactivation.
            </Alert>

            {sessions.map(sess => {
              const isEditing = editingSessId === sess.id;
              return (
                <Card key={sess.id} style={{ marginBottom:12, border: sess.status === 'active' ? `1.5px solid ${T.gold}44` : '1px solid #dde3ed' }}>
                  {/* Header row */}
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: isEditing ? 14 : 0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:8, height:8, borderRadius:'50%', background: sess.status === 'active' ? '#27ae60' : '#9aa3b8', flexShrink:0 }}/>
                      <div>
                        <div style={{ fontSize:13.5, fontWeight:600, color:'#1a2233' }}>{sess.label || 'Unnamed Session'}</div>
                        <div style={{ fontSize:11.5, color:'#5a6a88', fontFamily:"'JetBrains Mono',monospace" }}>
                          {sess.days.join(', ')} · {sess.startTime}–{sess.endTime} · {sess.timezone.match(/GMT[+-]\d+/)?.[0] || sess.timezone}
                        </div>
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:6 }}>
                      <button onClick={() => updateSession(sess.id, 'status', sess.status === 'active' ? 'inactive' : 'active')}
                        style={{ background:'transparent', border:`1px solid ${sess.status==='active'?T.red:T.green}44`, color:sess.status==='active'?T.red:T.green, padding:'4px 10px', borderRadius:6, cursor:'pointer', fontSize:11.5, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                        {sess.status === 'active' ? 'Deactivate' : 'Activate'}
                      </button>
                      <Btn small onClick={() => setEditingSessId(isEditing ? null : sess.id)}>{isEditing ? 'Collapse' : 'Edit'}</Btn>
                      <Btn small variant="danger" onClick={() => deleteSession(sess.id)}>Del</Btn>
                    </div>
                  </div>

                  {isEditing && (
                    <div>
                      {/* Session label */}
                      <Field label="Session Label">
                        <input value={sess.label} onChange={e => updateSession(sess.id, 'label', e.target.value)}
                          placeholder="e.g. Evening Group, Morning Group (Summer)"
                          style={{ width:'100%', padding:'8px 11px', border:'1px solid #dde3ed', borderRadius:7, background:'#f7f9fc', color:'#1a2233', fontSize:13, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:'none' }}/>
                      </Field>

                      {/* Days */}
                      <Field label="Days of Week">
                        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                          {ALL_DAYS.map(day => {
                            const active = sess.days.includes(day);
                            return (
                              <button key={day} onClick={() => toggleDay(sess.id, day)}
                                style={{ padding:'6px 10px', borderRadius:6, cursor:'pointer', fontSize:12, fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight: active?700:400,
                                  background: active ? T.gold+'22' : '#f7f9fc',
                                  border: `1.5px solid ${active ? T.gold : '#dde3ed'}`,
                                  color: active ? T.gold : '#5a6a88',
                                }}>
                                {day}
                              </button>
                            );
                          })}
                        </div>
                      </Field>

                      {/* Times */}
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                        <Field label="Start Time">
                          <input type="time" value={sess.startTime} onChange={e => updateSession(sess.id, 'startTime', e.target.value)}
                            style={{ width:'100%', padding:'8px 11px', border:'1px solid #dde3ed', borderRadius:7, background:'#f7f9fc', color:'#1a2233', fontSize:13, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:'none' }}/>
                        </Field>
                        <Field label="End Time">
                          <input type="time" value={sess.endTime} onChange={e => updateSession(sess.id, 'endTime', e.target.value)}
                            style={{ width:'100%', padding:'8px 11px', border:'1px solid #dde3ed', borderRadius:7, background:'#f7f9fc', color:'#1a2233', fontSize:13, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:'none' }}/>
                        </Field>
                      </div>

                      {/* Timezone */}
                      <Field label="Timezone">
                        <select value={sess.timezone} onChange={e => updateSession(sess.id, 'timezone', e.target.value)}
                          style={{ width:'100%', padding:'8px 11px', border:'1px solid #dde3ed', borderRadius:7, background:'#f7f9fc', color:'#1a2233', fontSize:13, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:'none' }}>
                          {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                        </select>
                      </Field>

                      {/* Status */}
                      <Field label="Status">
                        <div style={{ display:'flex', gap:8 }}>
                          {['active','inactive'].map(st => (
                            <button key={st} onClick={() => updateSession(sess.id, 'status', st)}
                              style={{ flex:1, padding:'8px', borderRadius:6, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:12.5, fontWeight: sess.status===st?700:400,
                                border: `1.5px solid ${sess.status===st?(st==='active'?T.green:T.red):'#dde3ed'}`,
                                background: sess.status===st?(st==='active'?'#f0faf4':'#fff5f5'):'transparent',
                                color: sess.status===st?(st==='active'?T.green:T.red):'#9aa3b8',
                              }}>
                              {st === 'active' ? '🟢 Active' : '⚫ Inactive'}
                            </button>
                          ))}
                        </div>
                      </Field>
                    </div>
                  )}
                </Card>
              );
            })}

            <button onClick={addSession}
              style={{ width:'100%', background:T.gold, color:'#fff', border:'none', padding:'11px 0', borderRadius:8, fontSize:13.5, fontWeight:600, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", marginTop:4 }}>
              + Add New Session
            </button>

            <div style={{ background:'#f7f9fc', border:'1px solid #dde3ed', borderRadius:8, padding:'12px 16px', marginTop:12 }}>
              <div style={{ fontSize:12.5, color:'#5a6a88', lineHeight:1.8 }}>
                <strong style={{color:'#1a2233'}}>How it works:</strong><br/>
                🟢 <strong>Active</strong> sessions appear on the Courses page under the IELTS Preparation card.<br/>
                ⚫ <strong>Inactive</strong> sessions are hidden from students but stay saved for easy reactivation.<br/>
                Changes save automatically to localStorage.
              </div>
            </div>
          </div>
        )}

        {/* ── ACADEMY ADMIN ── */}
        {tab === 'academy' && <AcademyAdmin/>}

        {/* ── LIVE SESSIONS ── */}
        {tab === 'live' && (
          <LiveSession isTeacher={true} prompts={prompts} />
        )}

      </div>
      <Footer branding={branding}/>
    </div>
  );
}
